import type { GuildWarWeek } from "@/lib/guild-war-api"

export type GuildWarSignupStateInput = {
  isDashboardReady: boolean
  isDashboardLoading: boolean
  isDashboardRefreshing: boolean
  hasPendingMutation: boolean
  weekState: GuildWarWeek["state"] | null
  isPermanentMember: boolean
  hasActiveWeeklySignup: boolean
  hasPendingPermanentApplication: boolean
}

export type GuildWarSignupActionState = {
  canEditWeeklySignup: boolean
  canApplyPermanent: boolean
  canWithdrawPermanent: boolean
  statusMessage: string
}

export const deriveGuildWarSignupActionState = (
  input: GuildWarSignupStateInput,
): GuildWarSignupActionState => {
  const canEditSignup = input.weekState === "signup_open"

  const canEditWeeklySignup =
    input.isDashboardReady &&
    canEditSignup &&
    !input.isPermanentMember &&
    !input.hasPendingPermanentApplication
  const canApplyPermanent =
    input.isDashboardReady &&
    !input.isPermanentMember &&
    !input.hasActiveWeeklySignup &&
    !input.hasPendingPermanentApplication
  const canWithdrawPermanent =
    input.isDashboardReady &&
    (input.isPermanentMember || input.hasPendingPermanentApplication)

  const statusMessage = input.isDashboardLoading
    ? "Loading your guild war state before actions become available."
    : input.isDashboardRefreshing
      ? "Refreshing your guild war state. Actions will re-enable when sync completes."
      : input.hasPendingMutation
        ? "Saving your update. Actions are temporarily locked to avoid conflicting requests."
        : !input.isDashboardReady
          ? "Guild war state is not ready yet. Try again shortly."
          : input.weekState
            ? canEditWeeklySignup
              ? "Signup is open. Pick Saturday, Sunday, or Both to lock your availability for this week."
              : input.isPermanentMember
                ? "You currently hold permanent guild war membership. Weekly signup is disabled."
                : input.hasPendingPermanentApplication
                  ? "You already have a pending permanent membership application. Withdraw it to return to weekly signup."
                  : "Signup is currently closed for this week. You can still update your default paths."
            : input.hasPendingPermanentApplication
              ? "No active week is available. Your permanent membership application is still pending."
              : input.isPermanentMember
                ? "No active week is available. Your permanent membership is still active."
                : "No active or upcoming week exists yet. You can still set your default paths and apply for permanent membership."

  return {
    canEditWeeklySignup,
    canApplyPermanent,
    canWithdrawPermanent,
    statusMessage,
  }
}
